import React, { useState } from 'react';
import { adminLogin } from '../services/adminApi';

export default function AdminLogin({ onLoginSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim()) {
      setError('Please enter username');
      return;
    }
    
    if (!password) {
      setError('Please enter password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const data = await adminLogin(username.trim(), password);
      
      // Store admin token (separate from landlord token)
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin));
      
      // Call parent callback
      onLoginSuccess(data.admin);
      
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      <style>{ADMIN_LOGIN_STYLES}</style>
      
      <div className="admin-login-container">
        
        {/* Logo */}
        <div className="admin-login-logo">
          <img src="/Hashvi_logo_white_no_BG.svg" alt="Hashvi" />
          <div className="admin-badge">ADMIN</div>
        </div>

        {/* Login Card */}
        <div className="admin-login-card">
          <h1 className="admin-login-title">Admin Login</h1>
          <p className="admin-login-subtitle">Hashvi Management System</p>

          <form onSubmit={handleSubmit}>
            
            {/* Username */}
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter admin username"
                disabled={loading}
                autoFocus
              />
            </div>

            {/* Password */}
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={loading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

          </form>

          {/* Security Notice */}
          <div className="security-notice">
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              🔒 Secure Admin Access
            </div>
            {username === 'admin' && password === '' && (
              <div style={{ fontSize: 11, color: '#f59e0b', background: '#fffbeb', padding: 8, borderRadius: 6 }}>
                ⚠️ Default password detected. Please change it after first login!
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="admin-login-footer">
          <a href="/" style={{ color: '#64748b', textDecoration: 'none', fontSize: 13 }}>
            ← Back to Landlord Login
          </a>
        </div>

      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1f4e79 0%, #163a5c 100%)',
    padding: '20px'
  }
};

const ADMIN_LOGIN_STYLES = `
  .admin-login-container {
    width: 100%;
    max-width: 420px;
  }

  .admin-login-logo {
    text-align: center;
    margin-bottom: 32px;
    position: relative;
  }

  .admin-badge {
    display: inline-block;
    margin-top: 12px;
    padding: 4px 16px;
    background: #f26522;
    color: white;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    border-radius: 12px;
  }

  .admin-login-card {
    background: white;
    border-radius: 16px;
    padding: 40px 32px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  .admin-login-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f4e79;
    margin: 0 0 4px 0;
    text-align: center;
  }

  .admin-login-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 32px 0;
    text-align: center;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #1f4e79;
    margin-bottom: 8px;
  }

  .form-input {
    width: 100%;
    padding: 12px 16px;
    font-size: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    transition: all 0.2s;
  }

  .form-input:focus {
    outline: none;
    border-color: #f26522;
    box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.1);
  }

  .form-input:disabled {
    background: #f8fafc;
    cursor: not-allowed;
  }

  .error-message {
    padding: 12px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #991b1b;
    font-size: 13px;
    margin-bottom: 20px;
  }

  .submit-btn {
    width: 100%;
    padding: 14px;
    font-size: 15px;
    font-weight: 600;
    color: white;
    background: linear-gradient(135deg, #f26522 0%, #d94e0d 100%);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .submit-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(242, 101, 34, 0.3);
  }

  .submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .security-notice {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
    text-align: center;
  }

  .admin-login-footer {
    margin-top: 24px;
    text-align: center;
  }

  @media (max-width: 480px) {
    .admin-login-card {
      padding: 32px 24px;
    }
  }
`;